import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import Dialog from "src/components/Dialog";

import { createStyles, makeStyles, alpha } from "@material-ui/core/styles";
import type { Theme } from "src/theme";

import { toast } from "react-toastify";

import { deleteUploadsApi } from "src/apiService";
import { Upload } from "src/interfaces";
import { getUploads } from "src/store/uploads/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { StatefulLoadableButton as LoadableButton } from "src/components/LoadableButton";
import { userRoleSelector } from "src/store/system/selector";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    selectedRow: {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
      },
    },
    errorButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      "&:hover": {
        backgroundColor: theme.palette.error.dark,
      },
    },
  })
);

const generateDisplayName = (fileName, fileType) => {
  if (fileName.includes("ebaySales")) {
    if (fileType === "sales") {
      return "ebay Sales";
    } else if (fileType === "transactions") {
      return "ebay Transactions";
    } else if (fileType === "returns") {
      return "ebay Returns";
    }
  }
  if (fileName.includes("ebayInventory")) return "ebay Inventory";
  if (fileName.includes("ebayFee")) return "ebay Fees";
  if (fileName.includes("poshmarkInventory")) return "poshmark Inventory";
  if (fileName.includes("poshmarkSales")) {
    if (fileType === "sales") {
      return "poshmark Sales";
    } else if (fileType === "transactions") {
      return "poshmark Transactions";
    } else if (fileType === "returns") {
      return "poshmark Returns";
    }
  }
  if (fileName.includes("mercariInventory")) return "mercari Inventory";
  if (fileName.includes("mercariSales")) return "mercari Sales";
  if (fileName.includes("amazonInventory")) return "Amazon Inventory";
  if (fileName.includes("amazonSales")) {
    if (fileType === "sales") {
      return "Amazon Sales";
    } else if (fileType === "transactions") {
      return "Amazon Transactions";
    } else if (fileType === "returns") {
      return "Amazon Returns";
    }
  }
  if (fileName.includes("etsySales")) {
    return "Etsy Sales";
  }
  if (fileName.includes("etsyReturns")) {
    return "Etsy Returns";
  }
  if (fileName.includes("etsyInventory")) {
    return "Etsy Inventory";
  }
  if (fileName.includes("etsyTransactions")) {
    return "Etsy Transactions";
  }
  if (fileName.includes("whatnotSales")) {
    if (fileType === "sales") {
      return "Whatnot Sales";
    } else if (fileType === "returns") {
      return "Whatnot Returns";
    }
  }
  if (fileName.includes("whatnotTransactions")) {
    return "Whatnot Transactions";
  }
  if (fileName.includes("vintedSales")) {
    if (fileType === "sales") {
      return "Vinted Sales";
    } else if (fileType === "returns") {
      return "Vinted Returns";
    }
  }
  if (fileName.includes("vintedInventory")) {
    return "Vinted Inventory";
  }
  if (fileName.includes("depopSales")) {
    if (fileType === "sales") {
      return "Depop Sales";
    } else if (fileType === "returns") {
      return "Depop Returns";
    }
  }
  if (fileName.includes("depopInventory")) {
    return "Depop Inventory";
  }
  return fileName;
};

function formatUploadLineCount({ n, nCreated }: Upload): string {
  if (nCreated != null) {
    return `${n} (created ${nCreated})`;
  }
  return `${n}`;
};

function ManageUploadsDialogContent({ onClose }: { onClose: () => void }) {
  const classes = useStyles();
  const unsortedUploads: Upload[] = useSelector(getUploads);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const userRole = useSelector(userRoleSelector);

  const uploads = useMemo(() => {
    const uploads = [...unsortedUploads];
    uploads.sort((a, b) => (b?.timestamp || 0) - (a?.timestamp || 0));
    return uploads;
  }, [unsortedUploads]);

  const toggleRow = (index: number) => {
    return setSelectedIndex((current) => {
      if (current === index) {
        return -1;
      }
      return index;
    });
  };

  const handleDeleteClick = async () => {
    await deleteUploadsApi([uploads[selectedIndex].id]);
    toast.success("Items successfully deleted.");
    onClose();
  };

  return (
    <>
      <DialogTitle>Review Uploads</DialogTitle>
      <DialogContent>
        <DialogContentText className="relative" tabIndex={-1} component="div">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>Upload time</TableCell>
                <TableCell>Number of lines</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((item, index) => (
                <TableRow
                  key={item.id}
                  className={clsx(
                    classes.tableRow,
                    index === selectedIndex && classes.selectedRow
                  )}
                  onClick={() => toggleRow(index)}
                >
                  <TableCell>
                    {generateDisplayName(item.filename, item.type)}
                  </TableCell>
                  <TableCell>
                    {new Date(item.timestamp || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>{formatUploadLineCount(item)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContentText>
      </DialogContent>
      <DialogActions className="px-7 py-5">
        <LoadableButton
          variant="contained"
          className={classes.errorButton}
          disabled={selectedIndex === -1 || userRole === "viewer"}
          onClick={handleDeleteClick}
        >
          Delete Uploaded Items
        </LoadableButton>
      </DialogActions>
    </>
  );
}

function ManageUploadsDialog({ open }: { open: boolean }) {
  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(setActiveDialog(""));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth={"sm"}>
      <ManageUploadsDialogContent onClose={handleClose} />
    </Dialog>
  );
}

export default ManageUploadsDialog;
